import { ChartLine, Flame, Utensils } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { APP_NAME } from '../../shared/brand';
import { WeightChart } from '../components/charts';
import { PublicShell } from '../components/PublicShell';
import { usePageMeta } from '../lib/meta';
import { SAMPLE_RANGES, SAMPLE_TODAY, SAMPLE_TREND_DAYS, sampleWeight, type SampleRangeId } from '../lib/sampleWeight';

const TITLE = `${APP_NAME} — weigh food, weigh in, see the trend`;
const DESCRIPTION =
  'Weigh food in grams, log daily weigh-ins, and follow a moving-average trend. Grams is a simple food and weight tracker.';

const FEATURES: { title: string; body: string; icon: ReactNode; tone: 'accent' | 'ai' | 'good' }[] = [
  {
    title: 'Gram-accurate food logging',
    body: 'Log food by weight. Grams are the base unit — the number on most labels — so a serving size can’t quietly change the calories.',
    icon: <Utensils size={20} />,
    tone: 'accent',
  },
  {
    title: 'Daily weigh-ins and the trend',
    body: 'Weigh in each morning. A 14-day moving average, with projections, shows where your weight is heading. A glass of water doesn’t count as progress.',
    icon: <ChartLine size={20} />,
    tone: 'ai',
  },
  {
    title: 'Daily foods and streaks',
    body: 'Repeat the foods you eat every day, and keep a streak of days you’ve logged. The habit stays in view.',
    icon: <Flame size={20} />,
    tone: 'good',
  },
];

/** The product Weight card, with invented weigh-ins. The bright dot is the only neon accent. */
function SampleWeightCard() {
  const [range, setRange] = useState<SampleRangeId>('30');
  const model = useMemo(() => sampleWeight(range), [range]);
  return (
    <section className="card card--pad mkt-weight" aria-label="Example weight chart with sample data">
      <div className="card__head">
        <h2 className="card__title">Weight</h2>
        <div className="seg" role="radiogroup" aria-label="Range">
          {SAMPLE_RANGES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="radio"
              aria-checked={range === item.id}
              className={`seg__btn ${range === item.id ? 'is-active' : ''}`}
              onClick={() => setRange(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <WeightChart
        points={model.points}
        trend={model.trend}
        goalKg={model.goalKg}
        unit="lb"
        today={SAMPLE_TODAY}
        forecastRate={model.forecastRate}
        forecastSe={model.forecastSe}
        height={220}
      />
      <div className="legend">
        <span>
          <span className="legend__swatch" style={{ background: 'var(--ink)' }} />
          {SAMPLE_TREND_DAYS}-day trend
        </span>
        <span>
          <span className="legend__swatch" style={{ background: 'var(--faint)', height: 6, width: 6, borderRadius: 3 }} />
          Weigh-ins
        </span>
        <span>
          <span className="legend__swatch" style={{ background: 'var(--fiber)' }} />
          Goal
        </span>
        <span>
          <span className="legend__swatch" style={{ background: 'var(--ai)' }} />
          Forecast
        </span>
      </div>
    </section>
  );
}

/** Home for signed-out visitors. Signed-in people still land on Today. */
export function SplashPage() {
  usePageMeta(TITLE, DESCRIPTION);
  return (
    <PublicShell>
      <div className="mkt-home" data-page="splash">
        <section className="mkt-hero">
          <div className="mkt-hero__copy">
            <p className="eyebrow">A food and weight log</p>
            <h1 className="mkt-hero__title">
              Weigh food in <span className="mkt-mark">grams</span>.
            </h1>
            <p className="mkt-hero__deck">Daily weigh-ins, and a moving-average trend that shows where your weight actually is.</p>
            <p className="mkt-hero__lede">Log what you eat by weight, step on the scale each morning, and trust the average instead of one noisy day.</p>
            <div className="mkt-hero__cta">
              <Link to="/signin" className="btn btn--accent btn--lg">
                Sign up
              </Link>
              <Link to="/signin" className="btn btn--ghost btn--lg">
                Log in
              </Link>
            </div>
            <p className="mkt-hero__more">
              <Link to="/about">About {APP_NAME}</Link>
              <span className="tiny muted">Invite only — use the email you were invited with.</span>
            </p>
          </div>
          <SampleWeightCard />
        </section>

        <section className="mkt-block" aria-labelledby="highlights">
          <h2 id="highlights" className="mkt-block__title">
            How {APP_NAME} helps
          </h2>
          <ul className="mkt-features">
            {FEATURES.map((feature) => (
              <li key={feature.title} className="card card--pad mkt-feature">
                <span className={`mkt-feature__icon mkt-feature__icon--${feature.tone}`}>{feature.icon}</span>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </PublicShell>
  );
}
